(() => {
  'use strict';

  const CONTEXT_SELECTOR = '[data-bibis-regional-context]';
  const SUPPORTED_REGIONS = new Set(['england', 'scotland', 'ireland']);
  const REGION_SUFFIX = { england: 'E', scotland: 'S', ireland: 'I' };
  const LOCATION_FAILURE_SOURCES = new Set(['error', 'timeout', 'regional-safety-timeout']);
  const LOGIN_URL = '/account/login';
  const TRADE_ACCOUNT_URL = '/pages/wholesell-sign-up-page';

  let tradeGateDialog = null;
  let tradeGateLastTrigger = null;

  const state = window.BibisRegionalState = window.BibisRegionalState || {
    tradeRegion: 'none',
    region: null,
    ready: false,
    source: 'pending'
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const normalizeRegion = (value) => SUPPORTED_REGIONS.has(normalize(value)) ? normalize(value) : 'none';

  function getVariantKind(title) {
    const normalized = String(title || '').trim();
    if (/^single$/i.test(normalized)) return { type: 'single', suffix: null };

    let match = normalized.match(/^case\s+([esi])$/i);
    if (match) return { type: 'case', suffix: match[1].toUpperCase() };

    match = normalized.match(/^default\s+([esi])$/i);
    if (match) return { type: 'default', suffix: match[1].toUpperCase() };

    return { type: 'other', suffix: null };
  }

  function getContext(element) {
    if (!element) return null;
    if (element.matches?.(CONTEXT_SELECTOR)) return element;
    return element.closest?.(CONTEXT_SELECTOR) || null;
  }

  function getVariants(context) {
    if (!context) return [];
    if (context.__bibisRegionalVariants) return context.__bibisRegionalVariants;

    const source = context.querySelector('[data-bibis-regional-variants]');
    if (!source) return [];

    try {
      const variants = JSON.parse(source.textContent || '[]');
      context.__bibisRegionalVariants = Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('[Regional variants] Unable to parse product variant data.', error);
      context.__bibisRegionalVariants = [];
    }

    return context.__bibisRegionalVariants;
  }

  function getTradeRegion(context) {
    const contextRegion = normalizeRegion(context?.dataset?.bibisTradeRegion);
    if (contextRegion !== 'none') return contextRegion;
    return normalizeRegion(state.tradeRegion);
  }

  function getEffectiveRegion(context) {
    const tradeRegion = getTradeRegion(context);
    if (tradeRegion !== 'none') return tradeRegion;
    if (!state.ready) return null;
    return normalizeRegion(state.region);
  }

  function getCurrentVariantId(context) {
    const formInput = context.querySelector('product-form form input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
    if (formInput?.value) return String(formInput.value);

    const stickySelect = context.querySelector('variant-selects.sticky-cart-select select');
    if (stickySelect?.value) return String(stickySelect.value);

    return null;
  }

  function isSingleCaseVariantAllowed(variant, tradeRegion) {
    const kind = getVariantKind(variant?.title);
    if (kind.type === 'single') return true;
    if (kind.type !== 'case') return false;
    if (tradeRegion === 'none') return false;
    return kind.suffix === REGION_SUFFIX[tradeRegion];
  }

  function getFirstAvailableOrFirst(variants) {
    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function getDesiredVariant(context) {
    const productType = context?.dataset?.bibisProductType || 'none';
    const variants = getVariants(context);
    if (!variants.length || productType === 'none') return null;

    const tradeRegion = getTradeRegion(context);

    if (productType === 'single_case') {
      const allowed = variants.filter((variant) => isSingleCaseVariantAllowed(variant, tradeRegion));
      const currentId = getCurrentVariantId(context);
      const currentAllowed = allowed.find((variant) => String(variant.id) === currentId);
      return currentAllowed || getFirstAvailableOrFirst(allowed);
    }

    if (productType === 'default_region') {
      const effectiveRegion = getEffectiveRegion(context);
      if (effectiveRegion === null) return null;

      if (effectiveRegion !== 'none') {
        const requiredSuffix = REGION_SUFFIX[effectiveRegion];
        const regionalVariant = variants.find((variant) => {
          const kind = getVariantKind(variant.title);
          return kind.type === 'default' && kind.suffix === requiredSuffix;
        });
        if (regionalVariant) return regionalVariant;
      }

      // A location-service failure must reveal a deterministic server fallback price.
      // Per the storefront requirement, use the product's first variant rather than
      // silently choosing a different regional price because it happens to be in stock.
      if (LOCATION_FAILURE_SOURCES.has(state.source)) return variants[0] || null;

      // A valid but unsupported/international location keeps the previous safe behavior.
      return getFirstAvailableOrFirst(variants);
    }

    return null;
  }

  function setPending(context, pending) {
    context.dataset.bibisRegionalPending = pending ? 'true' : 'false';

    context.querySelectorAll('[data-bibis-regional-price], [data-bibis-card-price], [data-bibis-dynamic-checkout]').forEach((element) => {
      element.dataset.bibisRegionalPending = pending ? 'true' : 'false';
      element.setAttribute('aria-busy', pending ? 'true' : 'false');
    });

    if (pending) {
      context.querySelectorAll('product-form [name="add"]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.bibisRegionPending = 'true';
      });
    } else {
      context.querySelectorAll('[data-bibis-region-pending="true"]').forEach((button) => {
        delete button.dataset.bibisRegionPending;
      });
    }
  }

  function filterSingleCaseControls(context) {
    const tradeRegion = getTradeRegion(context);
    const variants = getVariants(context);
    const allowedVariantIds = new Set(
      variants
        .filter((variant) => isSingleCaseVariantAllowed(variant, tradeRegion))
        .map((variant) => String(variant.id))
    );

    context.querySelectorAll('variant-radios input[type="radio"]').forEach((input) => {
      const kind = getVariantKind(input.value);
      if (kind.type !== 'single' && kind.type !== 'case') return;

      const allowed = kind.type === 'single' || (tradeRegion !== 'none' && kind.suffix === REGION_SUFFIX[tradeRegion]);
      input.disabled = !allowed;
      const wrapper = input.closest('.product-form__item');
      if (wrapper) wrapper.hidden = !allowed;
    });

    context.querySelectorAll('variant-selects:not(.sticky-cart-select) select option').forEach((option) => {
      const kind = getVariantKind(option.value);
      if (kind.type !== 'single' && kind.type !== 'case') return;

      const allowed = kind.type === 'single' || (tradeRegion !== 'none' && kind.suffix === REGION_SUFFIX[tradeRegion]);
      option.disabled = !allowed;
      option.hidden = !allowed;
    });

    context.querySelectorAll('variant-selects.sticky-cart-select select option').forEach((option) => {
      const allowed = allowedVariantIds.has(String(option.value));
      option.disabled = !allowed;
      option.hidden = !allowed;
    });
  }

  function hideDefaultRegionalPickers(context) {
    context.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
      picker.classList.add('bibis-regional-picker--hidden');
    });
  }


  function isCustomerLoggedIn(context) {
    return context?.dataset?.bibisCustomerLoggedIn === 'true';
  }

  function buildTradeCaseDemoButton(compact = false) {
    const wrapper = document.createElement('div');
    wrapper.dataset.bibisTradeCaseDemoWrapper = 'true';
    wrapper.className = compact
      ? 'bibis-trade-case-demo-wrapper bibis-trade-case-demo-wrapper--compact'
      : 'product-form__item product-form__item--zoom bibis-trade-case-demo-wrapper';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = compact
      ? 'bibis-trade-case-demo-button bibis-trade-case-demo-button--compact'
      : 'bibis-trade-case-demo-button';
    button.dataset.bibisTradeCaseDemo = 'true';
    button.textContent = 'Case';
    button.setAttribute('aria-haspopup', 'dialog');

    wrapper.appendChild(button);
    return wrapper;
  }

  function ensureTradeCaseDemo(context) {
    if (!context || context.dataset.bibisProductType !== 'single_case') return;

    const tradeRegion = getTradeRegion(context);
    const existingDemoWrappers = context.querySelectorAll('[data-bibis-trade-case-demo-wrapper]');

    // Approved regional trade customers always use the real Case variant control.
    if (tradeRegion !== 'none') {
      existingDemoWrappers.forEach((wrapper) => wrapper.remove());
      return;
    }

    // Button picker: place the demo Case control next to the real Single control.
    context.querySelectorAll('variant-radios').forEach((picker) => {
      if (picker.querySelector('[data-bibis-trade-case-demo-wrapper]')) return;

      const singleInput = [...picker.querySelectorAll('input[type="radio"]')]
        .find((input) => getVariantKind(input.value).type === 'single');
      const container = singleInput?.closest('.product-form__container');
      if (container) container.appendChild(buildTradeCaseDemoButton(false));
    });

    // Dropdown picker: keep the real Single select untouched and add a separate Case button.
    context.querySelectorAll('variant-selects:not(.sticky-cart-select)').forEach((picker) => {
      if (picker.nextElementSibling?.matches?.('[data-bibis-trade-case-demo-wrapper]')) return;
      const hasSingle = [...picker.querySelectorAll('option')]
        .some((option) => getVariantKind(option.value).type === 'single');
      if (!hasSingle) return;
      picker.insertAdjacentElement('afterend', buildTradeCaseDemoButton(true));
    });

    // Sticky add-to-cart gets the same gated Case action without creating a fake variant ID.
    context.querySelectorAll('variant-selects.sticky-cart-select').forEach((picker) => {
      if (picker.nextElementSibling?.matches?.('[data-bibis-trade-case-demo-wrapper]')) return;
      picker.insertAdjacentElement('afterend', buildTradeCaseDemoButton(true));
    });
  }

  function ensureTradeGateDialog(context) {
    const loggedIn = isCustomerLoggedIn(context);
    const loginUrl = context?.dataset?.bibisLoginUrl || LOGIN_URL;
    const tradeAccountUrl = context?.dataset?.bibisTradeAccountUrl || TRADE_ACCOUNT_URL;

    if (!tradeGateDialog) {
      tradeGateDialog = document.createElement('div');
      tradeGateDialog.className = 'bibis-trade-gate';
      tradeGateDialog.dataset.bibisTradeGate = 'true';
      tradeGateDialog.hidden = true;
      tradeGateDialog.innerHTML = `
        <button type="button" class="bibis-trade-gate__backdrop" data-bibis-trade-gate-close aria-label="Close"></button>
        <div class="bibis-trade-gate__dialog" role="dialog" aria-modal="true" aria-labelledby="BibisTradeGateTitle" aria-describedby="BibisTradeGateMessage">
          <button type="button" class="bibis-trade-gate__close" data-bibis-trade-gate-close aria-label="Close">&times;</button>
          <h2 class="bibis-trade-gate__title" id="BibisTradeGateTitle">Trade Account Needed</h2>
          <p class="bibis-trade-gate__message" id="BibisTradeGateMessage"></p>
          <div class="bibis-trade-gate__actions">
            <a href="${LOGIN_URL}" class="btn bibis-trade-gate__login" data-bibis-trade-login>Login</a>
            <a href="${TRADE_ACCOUNT_URL}" class="btn btn--secondary bibis-trade-gate__apply" data-bibis-trade-apply>Apply for Trade Account</a>
          </div>
        </div>`;
      document.body.appendChild(tradeGateDialog);
    }

    const message = tradeGateDialog.querySelector('.bibis-trade-gate__message');
    const loginButton = tradeGateDialog.querySelector('[data-bibis-trade-login]');
    const applyButton = tradeGateDialog.querySelector('[data-bibis-trade-apply]');

    if (loginButton) loginButton.href = loginUrl;
    if (applyButton) applyButton.href = tradeAccountUrl;

    if (loggedIn) {
      if (message) message.textContent = 'Trade Account Needed for selecting Case quantity. Your account is not currently approved for trade Case quantities. Apply for a trade account to continue.';
      if (loginButton) loginButton.hidden = true;
      if (applyButton) applyButton.hidden = false;
    } else {
      if (message) message.textContent = 'Trade Account Needed for selecting Case quantity. Please log in to an approved trade account or apply for a trade account.';
      if (loginButton) loginButton.hidden = false;
      if (applyButton) applyButton.hidden = false;
    }

    return tradeGateDialog;
  }

  function openTradeGate(context, trigger) {
    const dialog = ensureTradeGateDialog(context);
    if (!dialog) return;
    tradeGateLastTrigger = trigger || null;
    dialog.hidden = false;
    document.body.classList.add('bibis-trade-gate-open');
    window.requestAnimationFrame(() => dialog.querySelector('.bibis-trade-gate__close')?.focus());
  }

  function closeTradeGate() {
    if (!tradeGateDialog || tradeGateDialog.hidden) return;
    tradeGateDialog.hidden = true;
    document.body.classList.remove('bibis-trade-gate-open');
    tradeGateLastTrigger?.focus?.();
    tradeGateLastTrigger = null;
  }

  function syncPickerToVariant(context, variant) {
    const picker = context.querySelector('variant-radios, variant-selects:not(.sticky-cart-select)');
    if (!picker || !variant) return false;

    let changedControl = null;

    if (picker.tagName.toLowerCase() === 'variant-radios') {
      const groups = [...picker.querySelectorAll('.product-form__input')];
      variant.options.forEach((optionValue, index) => {
        const group = groups[index];
        if (!group) return;
        const input = [...group.querySelectorAll('input[type="radio"]')]
          .find((candidate) => candidate.value === optionValue && !candidate.disabled);
        if (input && !input.checked) {
          input.checked = true;
          changedControl = input;
        }
      });
    } else {
      const selects = [...picker.querySelectorAll('select')];
      variant.options.forEach((optionValue, index) => {
        const select = selects[index];
        if (!select) return;
        const option = [...select.options].find((candidate) => candidate.value === optionValue && !candidate.disabled);
        if (option && select.value !== optionValue) {
          select.value = optionValue;
          changedControl = select;
        }
      });
    }

    if (changedControl) {
      changedControl.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  function syncProductForms(context, variant) {
    if (!variant) return;

    context.querySelectorAll('form input[name="id"]').forEach((input) => {
      input.value = variant.id;
      input.disabled = false;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    context.querySelectorAll('variant-selects.sticky-cart-select select').forEach((select) => {
      const matchingOption = [...select.options].find((option) => String(option.value) === String(variant.id));
      if (matchingOption) select.value = String(variant.id);
    });

    context.querySelectorAll('product-form [name="add"]').forEach((button) => {
      const isAvailable = Boolean(variant.available);
      button.disabled = !isAvailable;
      if (isAvailable) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');

      const text = button.querySelector(':scope > span');
      if (text && window.variantStrings) {
        text.textContent = isAvailable ? window.variantStrings.addToCart : window.variantStrings.soldOut;
      }
    });
  }

  function syncProductPriceTemplate(context, variant) {
    const destination = context.querySelector('[data-bibis-regional-price]');
    const template = context.querySelector(`template[data-bibis-main-price-template="${variant?.id}"]`);
    if (!destination || !template) return;
    destination.innerHTML = template.innerHTML;
  }

  function syncVariantMedia(context, variant) {
    if (!variant?.featured_media?.id) return;
    const mediaGallery = context.querySelector('media-gallery');
    if (!mediaGallery || typeof mediaGallery.setActiveMedia !== 'function') return;

    const sectionId = context.dataset.bibisSection;
    if (!sectionId) return;
    mediaGallery.setActiveMedia(`${sectionId}-${variant.featured_media.id}`, true);
  }

  function applyProductContext(context) {
    const productType = context.dataset.bibisProductType || 'none';
    if (productType === 'none') return;

    if (productType === 'single_case') {
      filterSingleCaseControls(context);
      ensureTradeCaseDemo(context);
    }
    if (productType === 'default_region') hideDefaultRegionalPickers(context);

    const desiredVariant = getDesiredVariant(context);
    const pending = productType === 'default_region' && !desiredVariant;
    setPending(context, pending);
    if (pending || !desiredVariant) return;

    const pickerTriggered = syncPickerToVariant(context, desiredVariant);

    // Reveal the correct price immediately after region resolution. The theme's
    // existing variant-change fetch can still refresh the same markup afterward,
    // but the customer never sees the hidden server fallback in between.
    syncProductPriceTemplate(context, desiredVariant);

    if (!pickerTriggered) {
      syncProductForms(context, desiredVariant);
      syncVariantMedia(context, desiredVariant);
    }

    context.dataset.bibisSelectedVariantId = String(desiredVariant.id);
    setPending(context, false);
  }

  function updateCardPrice(context, variant) {
    const priceContainer = context.querySelector('[data-bibis-card-price]');
    const template = context.querySelector(`template[data-bibis-price-template="${variant?.id}"]`);
    if (!priceContainer || !template) return;

    // price.liquid already owns the outer wrapper used by the card. Replace only
    // its contents so existing direct-child styling/layout remains unchanged.
    const fragment = template.content.cloneNode(true);
    const renderedPrice = fragment.firstElementChild;
    priceContainer.innerHTML = renderedPrice ? renderedPrice.innerHTML : template.innerHTML;
    priceContainer.dataset.bibisRegionalPending = 'false';
  }

  function updateCardAvailability(context, variant) {
    if (context.dataset.bibisProductType !== 'default_region') return;

    const badgeContainer = context.querySelector('.badge__container');
    if (!badgeContainer) return;

    badgeContainer.querySelectorAll('.bibis-regional-soldout-badge').forEach((badge) => badge.remove());
    if (variant?.available) return;

    const badge = document.createElement('span');
    badge.className = 'badge badge--sold_out bibis-regional-soldout-badge';
    badge.textContent = window.variantStrings?.soldOut || 'Sold out';
    badgeContainer.appendChild(badge);
  }

  function applyCardContext(context) {
    const productType = context.dataset.bibisProductType || 'none';
    if (productType === 'none') return;

    const desiredVariant = getDesiredVariant(context);
    const pending = productType === 'default_region' && !desiredVariant;

    const priceContainer = context.querySelector('[data-bibis-card-price]');
    if (priceContainer) {
      priceContainer.dataset.bibisRegionalPending = pending ? 'true' : 'false';
      priceContainer.setAttribute('aria-busy', pending ? 'true' : 'false');
    }
    context.dataset.bibisRegionalPending = pending ? 'true' : 'false';

    if (pending || !desiredVariant) return;

    updateCardPrice(context, desiredVariant);
    updateCardAvailability(context, desiredVariant);
    context.dataset.bibisSelectedVariantId = String(desiredVariant.id);
  }

  function applyContext(context) {
    if (!context?.isConnected) return;
    const kind = context.dataset.bibisContextKind || 'product';
    if (kind === 'card') applyCardContext(context);
    else applyProductContext(context);
  }

  function initializeWithin(root = document) {
    const contexts = [];
    if (root.matches?.(CONTEXT_SELECTOR)) contexts.push(root);
    if (root.querySelectorAll) contexts.push(...root.querySelectorAll(CONTEXT_SELECTOR));
    contexts.forEach(applyContext);
  }

  function isFormVariantAllowed(element, variantId) {
    const context = getContext(element);
    if (!context) return true;

    const productType = context.dataset.bibisProductType || 'none';
    if (productType === 'none') return true;

    const variant = getVariants(context).find((item) => String(item.id) === String(variantId));
    if (!variant) return false;

    if (productType === 'single_case') {
      return isSingleCaseVariantAllowed(variant, getTradeRegion(context));
    }

    if (productType === 'default_region') {
      const desired = getDesiredVariant(context);
      return Boolean(desired && String(desired.id) === String(variant.id));
    }

    return true;
  }

  function refreshAll() {
    document.querySelectorAll(CONTEXT_SELECTOR).forEach(applyContext);
  }

  window.BibisRegionalVariants = {
    refresh: refreshAll,
    initializeWithin,
    isFormVariantAllowed,
    getDesiredVariant
  };

  document.addEventListener('click', (event) => {
    const demoTrigger = event.target.closest?.('[data-bibis-trade-case-demo]');
    if (demoTrigger) {
      event.preventDefault();
      const context = getContext(demoTrigger);
      if (context && getTradeRegion(context) === 'none') openTradeGate(context, demoTrigger);
      return;
    }

    if (event.target.closest?.('[data-bibis-trade-gate-close]')) {
      event.preventDefault();
      closeTradeGate();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeTradeGate();
  });

  document.addEventListener('DOMContentLoaded', () => {
    initializeWithin(document);

    // Location failures must never lock the storefront. get-location.liquid has
    // its own request timeout; this second guard covers a missing/broken response.
    window.setTimeout(() => {
      if (state.ready) return;
      state.region = 'none';
      state.ready = true;
      state.source = 'regional-safety-timeout';
      refreshAll();
    }, 5500);
  });
  document.addEventListener('bibis:regionchange', refreshAll);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeWithin(node);
      });
    });
  });

  if (document.documentElement) {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
